import { flowApi } from '.';
import { TAG_TYPES } from 'types/services';
import type {
  AcceptGroupReq,
  AcceptGroupRes,
  DeleteGroupReq,
  DeleteGroupRes,
  EditGroupReq,
  EditGroupRes,
  GetGroupRes,
  GetSharedUsersReq,
  GetSharedUsersRes,
  GetUsersInGroupReq,
  GetUsersInGroupRes,
  ShareGroupReq,
  ShareGroupRes,
} from 'types';

export const userGroupsService = flowApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getUserGroups: builder.query<GetGroupRes, void>({
      query: () => ({
        url: 'UserGroups',
        method: 'GET',
      }),
    }),
    deleteUserGroup: builder.mutation<DeleteGroupRes, DeleteGroupReq>({
      query: ({ userGroupId }) => ({
        url: `UserGroups/${userGroupId}`,
        method: 'DELETE',
      }),
    }),
    getUsersInGroup: builder.query<GetUsersInGroupRes, GetUsersInGroupReq>({
      query: ({ userGroupId, page, search, pageSize, orderBy, orderDesc, roleIds }) => ({
        url: `UserGroups/${userGroupId}/Users`,
        method: 'GET',
        params: {
          Page: page,
          Search: search,
          PageSize: pageSize,
          OrderBy: orderBy,
          OrderDesc: orderDesc,
          ...(roleIds !== undefined && (roleIds?.length === 0 ? { 'RoleIds[]': [''] } : { RoleIds: roleIds })),
        },
        paramsSerializer: (params: {
          Page: number;
          Search: string;
          PageSize: number;
          OrderBy: string;
          OrderDesc: boolean;
          'RoleIds[]'?: string[];
          RoleIds?: string[];
        }) => {
          return Object.entries(params)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                if (key === 'RoleIds[]') {
                  return `${key}=${encodeURIComponent(String(value[0] ?? ''))}`;
                }
                return value.map((item) => `${key}=${encodeURIComponent(String(item))}`).join('&');
              }
              return `${key}=${encodeURIComponent(String(value ?? ''))}`;
            })
            .join('&');
        },
      }),
      providesTags: [
        {
          type: TAG_TYPES.USER_GROUPS,
          id: 'LIST',
        },
      ],
    }),
    getSharedUsers: builder.query<GetSharedUsersRes, GetSharedUsersReq>({
      query: ({ userGroupId, page, search, pageSize, orderBy, orderDesc }) => ({
        url: `UserGroups/Shared`,
        method: 'GET',
        params: {
          UserGroupId: userGroupId,
          Page: page,
          Search: search,
          PageSize: pageSize,
          OrderBy: orderBy,
          OrderDesc: orderDesc,
        },
      }),
    }),
    shareUserGroup: builder.mutation<ShareGroupRes, ShareGroupReq>({
      query: ({ userGroupId, users }) => ({
        url: `UserGroups/${userGroupId}/Share`,
        method: 'PUT',
        body: {
          Users: users,
        },
      }),
    }),
    acceptUserGroup: builder.mutation<AcceptGroupRes, AcceptGroupReq>({
      query: ({ userGroupId }) => ({
        url: `UserGroups/${userGroupId}/Accept`,
        method: 'PUT',
      }),
    }),
    editUsersInGroup: builder.mutation<EditGroupRes, EditGroupReq>({
      query: (data) => ({
        url: 'UserGroups',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.USER_GROUPS,
          id: 'LIST',
        },
      ],
    }),
  }),
});
export const {
  useGetUserGroupsQuery,
  useGetSharedUsersQuery,
  useEditUsersInGroupMutation,
  useGetUsersInGroupQuery,
  useDeleteUserGroupMutation,
  useShareUserGroupMutation,
  useAcceptUserGroupMutation,
} = userGroupsService;
